import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { object as yupObject, ref as yupRef, string as yupString } from "yup";

import { Button, Modal, TelephoneMask, addToast } from "@icg360/design-system";

import { useKeystone, useUser } from "data/ss-data-hooks";
import { isError } from "data/ss-error";
import { logException, trackEvent } from "utils";

import styles from "./contact-item.module.scss";

const schema = yupObject()
  .shape({
    phone: yupString()
      .required("Please enter a valid phone number.")
      .matches(
        /^(\+0?1\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
        "Please enter a valid 10 digit phone number",
      ),
    phoneConfirm: yupString().when("phone", {
      is: (val) => val?.length > 0,
      then: (schema) =>
        schema
          .oneOf([yupRef("phone"), undefined, ""], "Phone numbers must match.")
          .required("Please enter a valid phone number."),
    }),
  })
  .required();

type EditContactPhoneFormFields = {
  phone: string;
  phoneConfirm?: string;
};

type EditContactPhoneButtonProps = {
  policyId: string;
  userEmail: string;
  onSuccess: (update: string) => void;
};

export const EditContactPhoneButton = ({
  policyId,
  userEmail,
  onSuccess,
}: EditContactPhoneButtonProps) => {
  const keystone = useKeystone();
  const user = useUser();

  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const {
    handleSubmit,
    register,
    formState: { errors, isValid },
    reset,
  } = useForm<EditContactPhoneFormFields>({
    resolver: yupResolver(schema),
    mode: "onBlur",
  });

  const onSave = async ({ phone }) => {
    setSaving(true);

    try {
      if (!user) return;
      const res = await keystone.updateInsuredInfo(
        policyId,
        userEmail,
        user.userId,
        {
          phone: phone.replace(/[\D]/g, ""),
        },
      );
      if (!isError(res)) {
        trackEvent("Update Phone Number - complete");
        setShowModal(false);
        addToast("Phone number updated", { icon: true });
        onSuccess(phone);
        reset();
      } else {
        throw res;
      }
    } catch (err) {
      trackEvent("Error Displayed (Update Contact Phone Number)", err);
      setSaving(false);
      addToast("We apologize. Something went wrong.", {
        icon: true,
        error: true,
      });
      logException(err);
    }
  };

  return (
    <>
      <Button
        appearance="link"
        size="xs"
        onPress={() => setShowModal(true)}
        data-testid="contact-phone-edit-btn"
      >
        Edit
      </Button>
      <Modal
        open={showModal}
        heading="Update phone number"
        onClose={() => setShowModal(false)}
        primaryAction="Save"
        onPrimaryAction={handleSubmit(onSave)}
        disablePrimary={!isValid}
        loadingPrimary={saving}
      >
        <div className={styles.modalContent}>
          <TelephoneMask
            label="Phone number"
            {...register("phone")}
            errorMessage={errors.phone?.message}
            isError={!!errors.phone}
            data-testid="contact-phone"
          />
          <TelephoneMask
            label="Confirm phone number"
            {...register("phoneConfirm")}
            errorMessage={errors.phoneConfirm?.message}
            isError={!!errors.phoneConfirm}
          />
        </div>
      </Modal>
    </>
  );
};
